import { LOCAL_STORAGE, SESSION_STORAGE } from 'constants/app';
import { Loading } from 'carbon-components-react';
import PortalModal from 'components/PortalModal';
import { useStore } from 'effector-react';
import { useQueryParams } from 'hooks/useQueryParams';
import { FC, useLayoutEffect } from 'react';
import { APPLICATIONS, CATALOG, ENTITIES, USER_PROFILE } from 'routes/constants';
import { $userStore } from 'store/user';
import { authenticateUserFX, fetchUserProfileFX } from 'store/user/effects';
import { generatePath, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseJwt } from 'utils';
import { fetchEntityProfileFX } from 'store/entities/effects';
import { setAccountUuidEV } from 'store/user/events';
import { setProfileTypeEV } from 'store/main/events';
import { ProfileTypeEnum } from 'store/main/interface';

export const WithAuthWaiting: FC = () => {
  const user = useStore($userStore) as any;
  const [{ authSessionId, notaryToken, notaryTokenKey }] = useQueryParams();
  const history = useHistory();
  const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
  const token_key = localStorage.getItem(LOCAL_STORAGE.TOKEN_KEY);
  const language = localStorage.getItem(LOCAL_STORAGE.LANG);
  const userProfileLoading = useStore(fetchUserProfileFX.pending);
  const authLoading = useStore(authenticateUserFX.pending);
  const hasTokens = (!!token && !!token_key) || (!!notaryToken && !!notaryTokenKey);
  const { i18n } = useTranslation();
  const shouldRequestUser = hasTokens && !user;
  const tokenInf = parseJwt(token || '');
  const POST_AUTH_REDIRECT_FINAL_RESTORE_PENDING = 'postAuthRedirectFinalRestorePending';
  const POST_AUTH_REDIRECT_RESTORED = 'postAuthRedirectRestored';

  const profileLoading = userProfileLoading;
  const isLoading = authLoading || profileLoading;

  useLayoutEffect(() => {
    if (authSessionId && !isLoading && !user && !notaryToken) {
      authenticateUserFX(authSessionId).then(() => {
        const redirectUrl =
          sessionStorage.getItem(SESSION_STORAGE.REDIRECT_URL) ||
          localStorage.getItem(SESSION_STORAGE.REDIRECT_URL);

        if (redirectUrl) {
          sessionStorage.setItem(POST_AUTH_REDIRECT_FINAL_RESTORE_PENDING, 'true');
          sessionStorage.removeItem(POST_AUTH_REDIRECT_RESTORED);

          history.replace(redirectUrl);
        }
      });
    }

    if (!authSessionId && shouldRequestUser) {
      if (notaryToken && notaryTokenKey) {
        localStorage.setItem(LOCAL_STORAGE.TOKEN, notaryToken);
        localStorage.setItem(LOCAL_STORAGE.TOKEN_KEY, notaryTokenKey);
        localStorage.setItem(LOCAL_STORAGE.LAST_TOKEN_REFRESH, new Date().toISOString());
      }

      fetchUserProfileFX({}).then((res) => {
        const userData = res.data;
        const savedEntityId = localStorage.getItem('uuid');
        const personId = userData?.accounts?.natural_person?.uuid;

        if (savedEntityId && savedEntityId !== personId) {
          setAccountUuidEV(savedEntityId);
          setProfileTypeEV(ProfileTypeEnum.LEGAL_ENTITY);

          fetchEntityProfileFX(savedEntityId);
        }

        const redirectUrl =
          sessionStorage.getItem(SESSION_STORAGE.REDIRECT_URL) ||
          localStorage.getItem(SESSION_STORAGE.REDIRECT_URL);

        const isAuthLandingPage =
          window.location.pathname === '/' || window.location.pathname.includes('/login');

        if (redirectUrl && isAuthLandingPage) {
          sessionStorage.setItem(POST_AUTH_REDIRECT_RESTORED, 'true');

          history.replace(redirectUrl);
        }
      });
    }
  }, [authSessionId]);

  useLayoutEffect(() => {
    if (authSessionId) {
      return;
    }

    const redirectUrl =
      sessionStorage.getItem(SESSION_STORAGE.REDIRECT_URL) ||
      localStorage.getItem(SESSION_STORAGE.REDIRECT_URL);

    const isAuthLandingPage =
      window.location.pathname === '/' || window.location.pathname.includes('/login');

    if (redirectUrl && isAuthLandingPage) {
      const [redirectPathname, redirectSearch = ''] = redirectUrl.split('?');

      sessionStorage.setItem(POST_AUTH_REDIRECT_RESTORED, 'true');

      history.replace({
        pathname: redirectPathname,
        search: redirectSearch ? `?${redirectSearch}` : '',
        state: {
          redirectSearch: redirectSearch ? `?${redirectSearch}` : '',
        },
      });

      return;
    }

    if (!isAuthLandingPage) {
      return;
    }

    if (!isLoading && user && !user.need_profile_submit && !tokenInf.legal_entity_uuid) {
      i18n.changeLanguage(user.ui_lang || language || 'lv');

      if (user.mode === 'internal') {
        history.push(CATALOG.LIST);

        return;
      }

      const submissions = window.location.pathname.includes('/submissions');
      const payment_completed = window.location.pathname.includes('/payment-completed');
      const submit_form = window.location.pathname.includes('/submit-form');
      const application_submit_uuid = localStorage.getItem('application_submit_uuid');
      const signing_document = window.location.pathname.includes('signing-document');
      const show_shared_payment = window.location.pathname.includes('/show_shared_payment');
      const create_basket = window.location.pathname.includes('/basket/create');
      const applications = window.location.pathname.includes('/applications');

      if (
        !submissions &&
        !payment_completed &&
        !submit_form &&
        !signing_document &&
        !show_shared_payment &&
        !create_basket &&
        !applications
      ) {
        if (application_submit_uuid) {
          history.push(generatePath(APPLICATIONS.SUBMIT_FORM, { uuid: application_submit_uuid }));
          localStorage.removeItem('application_submit_uuid');
        } else {
          if (user?.accounts?.legal_entities_count > 0) {
            if (user.need_profile_submit || user.need_profile_review) {
              history.push(USER_PROFILE);
            } else {
              history.push(ENTITIES.LIST);
            }
          } else {
            history.push(CATALOG.LIST);
          }
        }
      }
    }
  }, [profileLoading, isLoading, authSessionId]);

  if (isLoading) {
    return (
      <PortalModal>
        <Loading active />
      </PortalModal>
    );
  }

  return null;
};
